import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import type { Session, User } from "@supabase/supabase-js";
import { supabase } from "@/lib/supabase";

type AuthContextType = {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signInWithGoogle: () => Promise<void>;
  signOut: () => Promise<void>;
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    if (error) throw error;
  };

  const signInWithGoogle = async () => {
    const { error } = await supabase.auth.signInWithOAuth({
      provider: "google",
      options: {
        scopes:
          "https://www.googleapis.com/auth/gmail.modify https://www.googleapis.com/auth/calendar.events https://www.googleapis.com/auth/calendar.events.freebusy https://www.googleapis.com/auth/contacts.readonly https://www.googleapis.com/auth/contacts.other.readonly https://www.googleapis.com/auth/directory.readonly",
        redirectTo: window.location.origin,
        queryParams: {
          access_type: "offline",
          prompt: "consent",
        },
      },
    });
    if (error) throw error;
  };

  // After Google OAuth sign-in, store provider tokens as Gmail integration
  useEffect(() => {
    if (session?.provider_token && session?.user) {
      const providerToken = session.provider_token;
      const providerRefreshToken = session.provider_refresh_token;
      if (providerToken && providerRefreshToken) {
        // Fire and forget — store the Google tokens as a Gmail integration
        supabase.functions
          .invoke("store-google-integration", {
            body: {
              access_token: providerToken,
              refresh_token: providerRefreshToken,
            },
          })
          .catch(() => {
            // Silent fail — integration will be set up manually if this fails
          });
      }

      // Fire and forget — sync Google Contacts after sign-in
      supabase.functions
        .invoke("sync-google-contacts", {
          body: { user_id: session.user.id },
        })
        .catch(() => {
          // Silent fail — contacts sync is non-critical
        });

      // Send welcome email for brand-new signups (created within last 60s)
      const createdAt = new Date(session.user.created_at).getTime();
      const now = Date.now();
      if (now - createdAt < 60_000) {
        supabase.functions
          .invoke("send-welcome-email", {
            body: {
              email: session.user.email,
              name: session.user.user_metadata?.full_name || session.user.user_metadata?.name || "",
            },
          })
          .catch(() => {
            // Silent fail — welcome email is non-critical
          });
      }
    }
  }, [session?.provider_token]);

  const signOut = async () => {
    await supabase.auth.signOut();
  };

  return (
    <AuthContext.Provider
      value={{ user, session, loading, signIn, signInWithGoogle, signOut }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
};
